/**
 * Workspace > Alerts — Incident Timeline across all (filtered) products at
 * once, grouped by product. Unlike ProductAlertsSection's single-product
 * Timeline, this page fans out across the currently filtered product set:
 * fetchList first resolves which products match the search text (reusing
 * fetchProductsList, same as ProductsListPage), then pulls incident history
 * for exactly those product IDs via the new bulk endpoint and merges the two.
 *
 * v1 scope note: bounded to the first page of filtered products (no
 * cross-product infinite scroll yet), and filtering is search-only for now
 * (no org/status filter UI) — both are easy, deliberately deferred follow-ups.
 */
import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { ListView } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import type { GroupConfig, TimelineBarMapping } from "../../../ui/components/ListView/types";
import { useAuth, useRole } from "../../lib/auth/hooks";
import {
  dismissProductAlert,
  fetchProductsList,
  fetchProductsAlertHistoryBulk,
  type ProductListItem,
} from "../../lib/api/products";
import { alertTypeColor, severityLabel } from "./alertDisplay";
import { ProductGroupCard } from "./ProductGroupCard";
import { GroupedAlertSummaryCard } from "./GroupedAlertSummaryCard";
import { AlertContextPanelContent } from "./AlertContextPanelContent";
import * as pageStyles from "./AlertsListPage.css";

const PRODUCT_PAGE_SIZE = 50;

type AlertRow = {
  id: string;
  incidentId: string;
  productId: string;
  code: string;
  name: string;
  type: string | null;
  severity: number | null;
  description: string | null;
  openedAt: string;
  closedAt: string | null;
  active: boolean;
  resolvedSource: "device_event" | "stale_expired" | "manual_dismiss" | null;
  resolvedByUserId: string | null;
  resolvedByUserName: string | null;
  resolutionNote: string | null;
};

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AlertsListPage() {
  const { accessToken } = useAuth();
  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);
  const { hasRole } = useRole("super-admin");
  const isSuperAdmin = hasRole();

  /** Kept in sync with the latest fetchList response so renderGroupLabel (a
   *  stable callback) can always resolve current product info by ID without
   *  becoming a dependency that would otherwise force ListView to refetch. */
  const productsByIdRef = React.useRef<Map<string, ProductListItem>>(new Map());

  const [selectedAlertId, setSelectedAlertId] = React.useState<string | null>(null);
  /** Bumped after a successful dismiss so the Timeline refetches — this page
   *  otherwise has no live-refresh mechanism (no WS subscription per product,
   *  unlike ProductAlertsSection's single-product `dataGen`). */
  const [refreshToken, setRefreshToken] = React.useState(0);

  const columns = React.useMemo<ColumnDefinition<AlertRow>[]>(
    () => [
      { id: "code", label: "Code", render: (r) => <span>{r.code}</span> },
      { id: "name", label: "Name", render: (r) => <span>{r.name}</span> },
      { id: "severity", label: "Severity", render: (r) => <span>{severityLabel(r.severity)}</span> },
      { id: "openedAt", label: "Opened", render: (r) => <span>{formatWhen(r.openedAt)}</span> },
      {
        id: "closedAt",
        label: "Closed",
        render: (r) => <span>{r.active ? "Open" : formatWhen(r.closedAt)}</span>,
      },
    ],
    [],
  );

  const timelineMapping = React.useMemo<TimelineBarMapping<AlertRow>>(
    () => ({
      getStart: (r) => r.openedAt,
      getEnd: (r) => (r.active ? null : r.closedAt),
      getLabel: (r) => r.name,
      getColor: (r) => alertTypeColor(r.type),
      // No getRowKey — one track per incident, same as the single-product page.
    }),
    [],
  );

  const timelineGroup = React.useMemo<GroupConfig<AlertRow>>(
    () => ({
      getGroup: (r) => r.productId,
      display: "panel",
      renderGroupLabel: (productId) => (
        <ProductGroupCard
          productId={productId}
          product={productsByIdRef.current.get(productId)}
          accessToken={token}
        />
      ),
    }),
    [token],
  );

  // Default Timeline range: today, local midnight to next midnight — same
  // convention as the single-product page and Historical Data.
  const todayRangeFilter = React.useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return [{ id: "__timeRange", value: [start.toISOString(), end.toISOString()] }];
  }, []);

  const fetchAlerts = React.useCallback(
    async (params: ListParams): Promise<ListResult<AlertRow>> => {
      if (!token) return { data: [], total: 0 };

      const productsRes = await fetchProductsList(
        { page: 1, limit: PRODUCT_PAGE_SIZE, search: params.search },
        token,
      );
      const products = productsRes.data ?? [];
      productsByIdRef.current = new Map(products.map((p) => [p.id, p]));

      if (products.length === 0) return { data: [], total: 0 };

      const start = (params.filters?.timeRangeStart as string | undefined) ?? todayRangeFilter[0]!.value[0]!;
      const end = (params.filters?.timeRangeEnd as string | undefined) ?? todayRangeFilter[0]!.value[1]!;

      const historyRes = await fetchProductsAlertHistoryBulk(token, {
        ids: products.map((p) => p.id),
        start,
        end,
      });

      const rows: AlertRow[] = (historyRes.data ?? []).map((r, i) => ({
        ...r,
        id: `${r.productId}-${r.code}-${r.openedAt}-${i}`,
      }));

      return { data: rows, total: rows.length };
    },
    [token, todayRangeFilter],
  );

  /** view < edit < full — dismiss is an admin mutation, same tier the rest of
   *  the app uses for product edits (see the dismiss route's own comment in
   *  pro-api/telemetry.ts). Falls back to `true` for super-admins, who never
   *  get an `access` field on the /products list response (passthrough branch). */
  const canDismissAlert = React.useCallback(
    (productId: string) => {
      if (isSuperAdmin) return true;
      const tier = productsByIdRef.current.get(productId)?.access?.tier;
      return tier === "edit" || tier === "full";
    },
    [isSuperAdmin],
  );

  const handleDismissAlert = React.useCallback(
    async (productId: string, incidentId: string, reason: string) => {
      if (!token) throw new Error("Not authenticated");
      await dismissProductAlert(token, productId, incidentId, reason);
      setRefreshToken((g) => g + 1);
    },
    [token],
  );

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">Alerts</Text>
      </div>
      <div className={pageStyles.tableHost}>
        <ListView<AlertRow>
          listId="workspace-alerts-timeline"
          refreshToken={refreshToken}
          columns={columns}
          fetchList={fetchAlerts}
          enabledViews={["timeline"]}
          disableLocalPersistence
          fillContainer
          searchPlaceholder="Search by product name…"
          emptyTitle="No alerts"
          emptySubtitle="No incidents match the current filters."
          initialColumnFilters={todayRangeFilter}
          timeRangeFilter={{ enabled: true }}
          timelineMapping={timelineMapping}
          timelineGroup={timelineGroup}
          renderTimelineHoverCard={(rows, _cursorTimeMs, hoveredId, selectedTimelineAlertId) => (
            <GroupedAlertSummaryCard
              alerts={rows}
              getProduct={(productId) => productsByIdRef.current.get(productId)}
              accessToken={token}
              getColor={(a) => alertTypeColor(a.type)}
              highlightedId={hoveredId}
              selectedId={selectedTimelineAlertId}
            />
          )}
          timelineSelectedId={selectedAlertId}
          onTimelineBarSelect={(row) => setSelectedAlertId(row?.id ?? null)}
          renderTimelineAlertContextPanel={(row) => (
            <AlertContextPanelContent
              alert={row}
              canDismiss={canDismissAlert(row.productId)}
              onDismiss={(reason) => handleDismissAlert(row.productId, row.incidentId, reason)}
            />
          )}
        />
      </div>
    </div>
  );
}
